import React from "react";
import QuestionContent from "./QuestionContent";
import ThreadContent from "./ThreadContent";
import styles from "./ContentContainer.module.css";
import {
  type ChatReaction,
  type ChatReactionValue,
  type DislikeReasonCode,
} from "../types/chatFeedback";

// Define props for the ContentContainer component
interface ContentContainerProps {
  contentType: "thread" | "question";
  selectedItem?: {
    id?: string;
    title: string;
    type: "thread" | "question";
    timestamp: string;
  };
  realTimeData?: {
    questionText?: string;
    specialty?: string;
    status?: string;
    answer?: string;
    justification?: string;
    references?: Array<{
      citedText: string;
      reference: string;
      pageNumber: string;
    }>;
  } | null;
  isTransitioning?: boolean;
  threadMessages?: Array<{
    message_id: string;
    content: string | any;
    sender: "user" | "ai";
    timestamp: string;
    isToolCall?: boolean; // Tool-call bubbles hide logo/timestamp
    isAgentCommentary?: boolean;
    commentaryId?: string;
    isCommentaryStreaming?: boolean;
    generationId?: string;
    processing_status?: "pending" | "processing" | "complete" | "error";
    error_message?: string;
    progressSteps?: Array<{
      id: string;
      label: string;
      kind?: "status" | "commentary";
    }>;
    answer?: string;
    justification?: string;
    references?: Array<{
      citedText: string;
      reference: string;
      pageNumber: string;
      type?: string;
      details?: any;
    }>;
    savedNoteId?: string | null;
    userReaction?: ChatReaction | null;
  }>;
  isLoadingMessages?: boolean;
  processingStatus?: string;
  // External trigger to force thread smooth scroll to bottom
  scrollToBottomRequest?: number;
  reactionPendingMessageIds?: Set<string>;
  reactionErrors?: Record<string, string>;
  onReactionChange?: (
    messageId: string,
    reaction: ChatReactionValue | null,
    reasonCodes: DislikeReasonCode[]
  ) => Promise<boolean>;
}

/**
 * ContentContainer component - Acts as a container for different content types
 *
 * This component conditionally renders either QuestionContent or ThreadContent
 * based on the contentType prop and passes the appropriate ID to the component.
 */
const ContentContainer: React.FC<ContentContainerProps> = ({
  contentType,
  selectedItem,
  realTimeData,
  isTransitioning,
  threadMessages,
  isLoadingMessages,
  processingStatus,
  scrollToBottomRequest,
  reactionPendingMessageIds,
  reactionErrors,
  onReactionChange,
}) => {
  return (
    <div className={styles.contentContainer}>
      {/* Render QuestionContent if contentType is "question", otherwise render ThreadContent */}
      {contentType === "question" ? (
        <QuestionContent
          key={selectedItem?.id || "empty"}
          questionId={selectedItem?.id}
          realTimeData={realTimeData || undefined}
          isTransitioning={isTransitioning}
        />
      ) : (
        <ThreadContent
          threadId={selectedItem?.id}
          threadMessages={threadMessages}
          isLoading={isLoadingMessages}
          processingStatus={processingStatus}
          isTransitioning={isTransitioning}
          scrollToBottomRequest={scrollToBottomRequest}
          reactionPendingMessageIds={reactionPendingMessageIds}
          reactionErrors={reactionErrors}
          onReactionChange={onReactionChange}
        />
      )}
    </div>
  );
};

export default ContentContainer;
